import {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BarChart3,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Download,
  FileText,
  Maximize2,
  RotateCw,
  Save,
  Trash2,
  TriangleAlert,
  UploadCloud,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

import {
  api,
  ApiRequestError,
  type FinanceClient,
  type FinanceClientDashboard,
  type FinanceDashboard,
  type FinanceDocument,
  type FinanceDocumentDetail,
  type FinanceMonth,
} from "../api.js";
import { Notice, failure } from "../shared/ui.js";

type Route = "home" | "months" | "month" | "review" | "document";
export type FinanceAccessState = "loading" | "authorized" | "denied" | "error";
export type FinanceExtractionState =
  | "loading"
  | "ready"
  | "manual-review"
  | "unavailable";

export const financeAccessState = (error?: unknown): FinanceAccessState =>
  error === undefined
    ? "loading"
    : error instanceof ApiRequestError && error.status === 403
      ? "denied"
      : "error";

export const financeExtractionState = (
  availability?: {
    available: boolean;
    extraction?: { enabled: boolean; status: string };
  },
  failed = false,
): FinanceExtractionState => {
  if (failed || availability?.available === false) return "unavailable";
  if (availability === undefined) return "loading";
  return availability.extraction?.enabled === true &&
    availability.extraction.status !== "disabled"
    ? "ready"
    : "manual-review";
};

const basePath = "/invoice-scanning";
const financePath = () => location.pathname.slice(basePath.length) || "/";
const routeForPath = (): { name: Route; id?: string } => {
  const path = financePath();
  if (path === "/months") return { name: "months" };
  if (path === "/review") return { name: "review" };
  const month = /^\/months\/([^/]+)$/u.exec(path);
  if (month) return { name: "month", id: decodeURIComponent(month[1] ?? "") };
  const document = /^\/documents\/([^/]+)$/u.exec(path);
  if (document)
    return { name: "document", id: decodeURIComponent(document[1] ?? "") };
  return { name: "home" };
};
const money = (totals: Record<string, string>) =>
  Object.entries(totals)
    .map(([currency, value]) => `${currency} ${value}`)
    .join(" · ") || "-";
const status = (value: FinanceDocument["review_status"]) =>
  value.replaceAll("_", " ");
interface FinanceIssue {
  label: string;
  detail: string;
}

type ReviewStatus = FinanceDocument["review_status"];
interface Bank {
  iban: string | null;
  bic_swift: string | null;
  bank_name: string | null;
  account_number: string | null;
}
interface Party {
  name: string | null;
  address: string | null;
  country: string | null;
  company_id: string | null;
  tax_id: string | null;
  vat_id: string | null;
  bank: Bank;
}
interface LineItem {
  description: string | null;
  quantity: string | null;
  unit: string | null;
  unit_price: string | null;
  net_amount: string | null;
  vat_rate: string | null;
  vat_amount: string | null;
  gross_amount: string | null;
  account_number: string | null;
  account_name: string | null;
}
interface ExtractionPayload extends Record<string, unknown> {
  source_file: string | null;
  document_type: string | null;
  document_date: string | null;
  invoice_or_reference_number: string | null;
  seller: Party;
  buyer: Party;
  net_amount: string | null;
  vat_rate: string | null;
  vat_amount: string | null;
  gross_amount: string | null;
  currency: string | null;
  transaction_direction: "income" | "expense" | "unknown";
  account_number: string | null;
  account_name: string | null;
  account_reasoning: string | null;
  account_confidence: number | null;
  classification_status: "classified" | "needs_review" | "manual";
  line_items: LineItem[];
}

const emptyBank: Bank = {
  iban: null,
  bic_swift: null,
  bank_name: null,
  account_number: null,
};
const emptyParty: Party = {
  name: null,
  address: null,
  country: null,
  company_id: null,
  tax_id: null,
  vat_id: null,
  bank: emptyBank,
};
const emptyPayload: ExtractionPayload = {
  source_file: null,
  document_type: null,
  document_date: null,
  invoice_or_reference_number: null,
  seller: emptyParty,
  buyer: emptyParty,
  net_amount: null,
  vat_rate: null,
  vat_amount: null,
  gross_amount: null,
  currency: null,
  transaction_direction: "unknown",
  account_number: null,
  account_name: null,
  account_reasoning: null,
  account_confidence: null,
  classification_status: "needs_review",
  line_items: [],
};

const normalizePayload = (
  payload: Record<string, unknown> | null | undefined,
  filename: string,
): ExtractionPayload => {
  const source = (payload || {}) as Partial<ExtractionPayload>;
  return {
    ...emptyPayload,
    ...source,
    source_file: source.source_file || filename,
    seller: {
      ...emptyParty,
      ...(source.seller || {}),
      bank: { ...emptyBank, ...(source.seller?.bank || {}) },
    },
    buyer: {
      ...emptyParty,
      ...(source.buyer || {}),
      bank: { ...emptyBank, ...(source.buyer?.bank || {}) },
    },
    transaction_direction: source.transaction_direction || "unknown",
    classification_status: source.classification_status || "needs_review",
    line_items: source.line_items || [],
  };
};
const setDeep = <T,>(source: T, path: string, value: string | null): T => {
  const next = structuredClone(source);
  const parts = path.split(".");
  let cursor = next as Record<string, unknown>;
  for (const part of parts.slice(0, -1))
    cursor = cursor[part] as Record<string, unknown>;
  cursor[parts.at(-1) || ""] = value;
  return next;
};
const emptyLineItem = (): LineItem => ({
  description: null,
  quantity: null,
  unit: null,
  unit_price: null,
  net_amount: null,
  vat_rate: null,
  vat_amount: null,
  gross_amount: null,
  account_number: null,
  account_name: null,
});
const confidenceLabel = (value: number | null) =>
  value === null || value === undefined
    ? "No confidence score."
    : `${Math.round(value * 100)}% confidence.`;
const fieldStatus = (value: string | null, critical: boolean): ReviewStatus =>
  value?.trim() ? "complete" : critical ? "manual_required" : "needs_check";
export const financeDocumentIssues = (
  document: FinanceDocument & { month?: string },
  month?: string,
): FinanceIssue[] => {
  const issues: FinanceIssue[] = [];
  if (document.processing_status === "failed")
    issues.push({
      label: "Failed",
      detail: "Extraction failed for this document.",
    });
  if (["queued", "processing"].includes(document.processing_status))
    issues.push({
      label: "Pending",
      detail: "Extraction is not finished yet.",
    });
  if (!document.document_date)
    issues.push({ label: "No date", detail: "The document date is missing." });
  else if (month && !document.document_date.startsWith(month))
    issues.push({
      label: "Month mismatch",
      detail: `Document date does not match ${month}.`,
    });
  if (!document.reference)
    issues.push({
      label: "No reference",
      detail: "Invoice or reference number is missing.",
    });
  if (!document.seller || !document.buyer)
    issues.push({
      label: "Party missing",
      detail: "Seller or buyer name is missing.",
    });
  if (!document.currency || !document.gross_amount)
    issues.push({
      label: "No total",
      detail: "Currency or gross total is missing.",
    });
  if (
    !document.account_number ||
    document.classification_status === "needs_review"
  )
    issues.push({
      label: "Account",
      detail: "Chart-of-accounts classification needs review.",
    });
  if (document.transaction_direction === "unknown")
    issues.push({
      label: "Direction",
      detail: "Income/expense direction is unknown.",
    });
  return issues;
};
const navigate = (path: string) => {
  window.history.pushState({}, "", `${basePath}${path}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
};
const download = (blob: Blob, name: string) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = name;
  link.click();
  URL.revokeObjectURL(href);
};

export function FinanceWorkspace() {
  const [route, setRoute] = useState(routeForPath);
  const [clients, setClients] = useState<FinanceClient[]>([]);
  const [clientId, setClientId] = useState("");
  const [dashboard, setDashboard] = useState<FinanceDashboard>();
  const [detail, setDetail] = useState<FinanceDocumentDetail>();
  const [message, setMessage] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [access, setAccess] = useState<FinanceAccessState>("loading");
  const [extraction, setExtraction] =
    useState<FinanceExtractionState>("loading");
  const input = useRef<HTMLInputElement>(null);
  const clientRoute = route.name === "home" && financePath() === "/client";
  const refresh = async () => {
    const data = await api.financeDashboard(
      clientId || undefined,
      route.name === "month" ? route.id : undefined,
    );
    setDashboard(data);
    setMessage(undefined);
  };
  useEffect(() => {
    const update = () => {
      setMessage(undefined);
      setRoute(routeForPath());
    };
    window.addEventListener("popstate", update);
    return () => {
      window.removeEventListener("popstate", update);
    };
  }, []);
  useEffect(() => {
    if (clientRoute) return;
    void api
      .financeClients()
      .then(({ clients: next }) => {
        const nextClients = Array.isArray(next) ? next : [];
        setAccess("authorized");
        setClients(nextClients);
        setClientId((current) => current || nextClients[0]?.id || "");
      })
      .catch((error: unknown) => {
        setAccess(financeAccessState(error));
      });
  }, [clientRoute]);
  useEffect(() => {
    void api
      .financeAvailability()
      .then((availability) => {
        setExtraction(financeExtractionState(availability));
      })
      .catch(() => {
        setExtraction("unavailable");
      });
  }, []);
  useEffect(() => {
    if (clientRoute || access !== "authorized" || !clientId) return;
    void refresh().catch((error: unknown) => {
      setMessage(failure(error));
    });
  }, [clientId, route.name, route.id, clientRoute, access]);
  useEffect(() => {
    if (route.name !== "document" || !route.id || !clientId) return;
    setDetail(undefined);
    void api
      .financeDocument(route.id, clientId)
      .then(setDetail)
      .catch((error: unknown) => {
        setMessage(failure(error));
      });
  }, [route, clientId]);
  const months = dashboard?.months ?? [];
  const active =
    route.name === "month"
      ? months.find((month) => month.month === route.id)
      : dashboard?.active_month;
  const review = useMemo(
    () =>
      months
        .flatMap((month) =>
          month.documents_list.map((document) => ({
            ...document,
            month: month.month,
          })),
        )
        .filter((document) => document.review_status !== "complete"),
    [months],
  );
  useEffect(() => {
    if (
      clientRoute ||
      !months.some((month) =>
        month.documents_list.some(
          (document) =>
            document.processing_status === "queued" ||
            document.processing_status === "processing",
        ),
      )
    )
      return;
    const timer = window.setInterval(() => {
      void refresh().catch(() => undefined);
    }, 3000);
    return () => {
      window.clearInterval(timer);
    };
  }, [clientRoute, months, clientId, route.name, route.id]);
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setMessage(undefined);
    try {
      await action();
    } catch (error) {
      setMessage(failure(error));
    } finally {
      setBusy(false);
    }
  };
  const upload = (files: FileList | null) =>
    void run(async () => {
      if (!files?.length) return;
      if (!clientId) {
        setMessage("Select a client before uploading Finance documents.");
        return;
      }
      await api.financeUpload(clientId, Array.from(files));
      await refresh();
      setMessage(
        extraction === "ready"
          ? "Upload accepted for extraction."
          : "Upload accepted. Automatic extraction is disabled; each document requires manual review.",
      );
      navigate("/review");
    });
  const exportMonth = (month: string) =>
    void run(async () => {
      await api.financeMonthExportGenerate(month, clientId || undefined);
      const file = await api.financeMonthExportDownload(
        month,
        clientId || undefined,
      );
      download(file.blob, file.name);
    });
  const remove = (document: FinanceDocument) =>
    void run(async () => {
      if (!window.confirm(`Delete ${document.filename}?`)) return;
      if (!clientId) {
        setMessage("Select a client before changing Finance documents.");
        return;
      }
      await api.financeDeleteDocument(document.id, clientId);
      await refresh();
      navigate("/review");
    });
  return (
    <section
      className={`finance-workspace finance-operational-workspace frozen-ledger-finance${route.name === "document" ? " document-view" : ""}`}
      data-page-layout="fiori-floorplan-finance"
      aria-label="Invoice scanning finance workspace"
    >
      <nav className="finance-subnav" aria-label="Invoice scanning navigation">
        {(
          [
            ["/", "Home"],
            ["/months", "Months"],
            [
              "/review",
              `Review Queue${review.length ? ` (${review.length})` : ""}`,
            ],
            ["/client", "Client workspace"],
          ] as [string, string][]
        ).map(([path, label]) => (
          <button
            key={path}
            type="button"
            className={
              financePath() === path ||
              (path === "/months" && route.name === "month")
                ? "active"
                : ""
            }
            onClick={() => {
              navigate(path);
            }}
          >
            {label}
          </button>
        ))}
      </nav>
      {!clientRoute && clients.length > 0 && (
        <div className="finance-scope-bar">
          <label className="finance-client">
            Select client
            <select
              value={clientId}
              onChange={(event) => {
                setClientId(event.target.value);
              }}
            >
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {message && <Notice error>{message}</Notice>}
      {clientRoute && <ClientWorkspace extraction={extraction} />}
      {!clientRoute && access === "loading" && (
        <Notice>Checking your Finance workspace role…</Notice>
      )}
      {!clientRoute && access === "denied" && (
        <Notice error>
          Finance workspace access is not assigned to your local PayDay role.
          Ask a PayDay administrator to assign an accountant Finance workspace
          role, then refresh this page.
        </Notice>
      )}
      {!clientRoute && access === "error" && (
        <Notice error>
          Finance workspace access could not be checked. Refresh this page and
          try again.
        </Notice>
      )}
      {access === "authorized" && extraction === "loading" && (
        <Notice>Checking automatic extraction availability…</Notice>
      )}
      {route.name !== "document" &&
        access === "authorized" &&
        extraction === "manual-review" && (
          <Notice>
            Automatic extraction is disabled. Uploaded documents require manual
            review by an accountant.
          </Notice>
        )}
      {route.name !== "document" &&
        access === "authorized" &&
        extraction === "unavailable" && (
          <Notice error>
            Finance extraction availability could not be confirmed. Upload is
            unavailable until the service is available.
          </Notice>
        )}
      {route.name === "home" && !clientRoute && access === "authorized" && (
        <Home
          active={dashboard?.active_month ?? null}
          months={months}
          busy={busy}
          uploadDisabled={
            !clientId ||
            extraction === "loading" ||
            extraction === "unavailable"
          }
          extraction={extraction}
          input={input}
          onUpload={upload}
        />
      )}
      {route.name === "months" && access === "authorized" && (
        <Months months={months} busy={busy} onExport={exportMonth} />
      )}
      {route.name === "month" && access === "authorized" && (
        <Month
          month={active ?? undefined}
          busy={busy}
          onExport={exportMonth}
          onRemove={remove}
        />
      )}
      {route.name === "review" && access === "authorized" && (
        <div className="page">
          <header className="page-header">
            <div>
              <h1>Review Queue</h1>
            </div>
            <div className="page-actions">
              <span className="subtle-count">{review.length} documents</span>
            </div>
          </header>
          <Documents
            title="Documents requiring attention"
            documents={review}
            busy={busy}
            onRemove={remove}
            showMonth
          />
        </div>
      )}
      {route.name === "document" && access === "authorized" && (
        <DocumentReview
          detail={detail ?? undefined}
          clientId={clientId}
          busy={busy}
          onSave={(payload) =>
            void run(async () => {
              if (!detail || !clientId) return;
              await api.financeSaveDocument(detail.id, clientId, payload);
              const next = await api.financeDocument(detail.id, clientId);
              setDetail(next);
              await refresh();
              setMessage("Changes saved.");
            })
          }
          onStatus={(payload, review_status) =>
            void run(async () => {
              if (!detail || !clientId) return;
              await api.financeSaveDocument(detail.id, clientId, payload);
              await api.financeDocumentStatus(
                detail.id,
                clientId,
                review_status,
              );
              const next = await api.financeDocument(detail.id, clientId);
              setDetail(next);
              await refresh();
              setMessage(`Marked as ${status(review_status)}.`);
            })
          }
        />
      )}
    </section>
  );
}

function Home({
  active,
  months,
  busy,
  uploadDisabled,
  extraction,
  input,
  onUpload,
}: {
  active: FinanceMonth | null;
  months: FinanceMonth[];
  busy: boolean;
  uploadDisabled: boolean;
  extraction: FinanceExtractionState;
  input: React.RefObject<HTMLInputElement | null>;
  onUpload: (files: FileList | null) => void;
}) {
  const values = months.slice(0, 8).reverse();
  const max = Math.max(
    1,
    ...values.map((month) =>
      Math.abs(Number(Object.values(month.net_result)[0] || 0)),
    ),
  );
  return (
    <div className="page home-page">
      <section className="home-hero panel">
        <div className="home-copy">
          <h1>Finance overview</h1>
          <p>
            Upload invoices and review only the documents that need attention.
          </p>
        </div>
        <div className="upload-panel">
          <label
            className="dropzone"
            aria-label={
              extraction === "manual-review"
                ? "Upload documents for manual review"
                : "Upload Finance documents"
            }
          >
            <input
              ref={input}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.webp"
              disabled={busy || uploadDisabled}
              onChange={(event) => {
                onUpload(event.currentTarget.files);
              }}
            />
            <span className="upload-icon">
              <UploadCloud size={20} aria-hidden="true" />
            </span>
            <strong>
              {busy
                ? "Working..."
                : extraction === "loading"
                  ? "Checking extraction..."
                  : extraction === "unavailable"
                    ? "Extraction unavailable"
                    : extraction === "manual-review"
                      ? "Upload for manual review"
                      : "Upload documents"}
            </strong>
            <span>
              {extraction === "manual-review"
                ? "Drop an invoice here; an accountant will review it manually."
                : "Drop PDFs, invoices, or receipt photos here."}
            </span>
          </label>
        </div>
      </section>
      <Stats month={active} />
      <section className="panel chart-panel">
        <div className="section-header">
          <div>
            <h2>Monthly result</h2>
            <p>Income minus expenses, grouped by extracted document date.</p>
          </div>
          <BarChart3 size={20} />
        </div>
        <div className="bar-chart">
          {values.length ? (
            values.map((month) => {
              const total = Object.values(month.net_result)[0] || "0";
              return (
                <button
                  key={month.month}
                  type="button"
                  className="bar-item"
                  onClick={() => {
                    navigate(`/months/${month.month}`);
                  }}
                >
                  <span className="bar-value">{total}</span>
                  <span className="bar-track">
                    <span
                      style={{
                        height: `${Math.max(8, (Math.abs(Number(total)) / max) * 100)}%`,
                      }}
                    />
                  </span>
                  <strong>{month.month.slice(2)}</strong>
                </button>
              );
            })
          ) : (
            <div className="empty-state">No monthly totals yet.</div>
          )}
        </div>
      </section>
    </div>
  );
}
function ClientWorkspace({
  extraction,
}: {
  extraction: FinanceExtractionState;
}) {
  const [dashboard, setDashboard] = useState<FinanceClientDashboard>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const input = useRef<HTMLInputElement>(null);
  const refresh = async () => {
    try {
      setDashboard(await api.financeClientDashboard());
      return true;
    } catch (error: unknown) {
      setMessage(failure(error));
      return false;
    }
  };
  useEffect(() => {
    void refresh();
  }, []);
  const run = (action: () => Promise<void>) => {
    setBusy(true);
    setMessage(undefined);
    void action()
      .catch((error: unknown) => {
        setMessage(failure(error));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const upload = (files: FileList | null) => {
    run(async () => {
      if (!files?.length) return;
      await api.financeClientUpload(Array.from(files));
      const refreshed = await refresh();
      if (refreshed) setMessage("Documents uploaded for secure scanning.");
    });
  };
  const remove = (id: string, filename: string) => {
    run(async () => {
      if (!window.confirm(`Delete ${filename}?`)) return;
      await api.financeClientDeleteDocument(id);
      await refresh();
    });
  };
  const summary = dashboard?.summary;
  return (
    <section
      className="finance-client-workspace"
      aria-labelledby="finance-client-title"
    >
      <header className="finance-heading">
        <div>
          <h2 id="finance-client-title">Finance client workspace</h2>
          <p>
            Upload documents and follow their processing status. Your accountant
            reviews extracted values.
          </p>
        </div>
      </header>
      {message && <Notice error>{message}</Notice>}
      <div className="finance-hero">
        <div>
          <h3>{dashboard?.client?.name || "Your finance documents"}</h3>
          <p>
            Documents remain in your PayDay account and are available to your
            assigned accountant.
          </p>
        </div>
        <div className="finance-upload">
          <input
            ref={input}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.webp"
            onChange={(event) => {
              upload(event.currentTarget.files);
            }}
          />
          <button
            type="button"
            disabled={
              busy || extraction === "loading" || extraction === "unavailable"
            }
            onClick={() => {
              input.current?.click();
            }}
          >
            {busy
              ? "Uploading…"
              : extraction === "loading"
                ? "Checking extraction…"
                : extraction === "unavailable"
                  ? "Extraction unavailable"
                  : extraction === "manual-review"
                    ? "Upload for manual review"
                    : "Upload documents"}
          </button>
          <span>
            {extraction === "manual-review"
              ? "Automatic extraction is disabled; every upload needs accountant review."
              : "PDFs, invoices, and receipt photos."}
          </span>
        </div>
      </div>
      <div className="finance-stats">
        {(
          [
            "uploaded",
            "scanning",
            "processing",
            "needs_review",
            "archived",
            "failed",
          ] as const
        ).map((key) => (
          <div className="finance-card" key={key}>
            <span>{key.replaceAll("_", " ")}</span>
            <strong>{String(summary?.[key] ?? 0)}</strong>
          </div>
        ))}
      </div>
      <section className="finance-card finance-table">
        <h3>Your documents</h3>
        <table>
          <thead>
            <tr>
              <th>File</th>
              <th>Uploaded</th>
              <th>Status</th>
              <th>Month</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {dashboard?.documents.map((document) => (
              <tr key={document.id}>
                <td>
                  {document.filename}
                  {document.error_message && (
                    <small>{document.error_message}</small>
                  )}
                </td>
                <td>{document.uploaded_at}</td>
                <td>
                  <span className={`finance-status ${document.review_status}`}>
                    {document.client_status}
                  </span>
                </td>
                <td>{document.document_month || "-"}</td>
                <td>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      remove(document.id, document.filename);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {!dashboard?.documents.length && (
              <tr>
                <td colSpan={5}>No documents uploaded yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </section>
  );
}
function Stats({ month }: { month: FinanceMonth | null | undefined }) {
  return (
    <section className="stats-grid">
      <StatCard title="Current month" value={month?.month || "-"} />
      <StatCard title="Documents" value={String(month?.documents || 0)} />
      <StatCard
        title="Income net"
        value={money(month?.net_income || {})}
        tone="complete"
      />
      <StatCard
        title="Expenses net"
        value={money(month?.net_expenses || {})}
        tone="manual_required"
      />
      <StatCard
        title="Net result"
        value={money(month?.net_result || {})}
        tone="needs_check"
      />
      <StatCard title="Gross total" value={money(month?.totals || {})} />
    </section>
  );
}
function StatCard({
  title,
  value,
  tone,
}: {
  title: string;
  value: string;
  tone?: ReviewStatus;
}) {
  return (
    <div className={`stat-card ${tone || ""}`}>
      <span>{title}</span>
      <strong>{value}</strong>
    </div>
  );
}
function Months({
  months,
  busy,
  onExport,
}: {
  months: FinanceMonth[];
  busy: boolean;
  onExport: (month: string) => void;
}) {
  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Months</h1>
        </div>
        <div className="page-actions">
          <span className="subtle-count">{months.length} months</span>
        </div>
      </header>
      <section className="panel">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Documents</th>
                <th>Complete</th>
                <th>Needs check</th>
                <th>Manual</th>
                <th>Income</th>
                <th>Expenses</th>
                <th>Net result</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {months.map((month) => (
                <tr key={month.month}>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      aria-label={`Open ${month.month}`}
                      onClick={() => {
                        navigate(`/months/${month.month}`);
                      }}
                    >
                      {month.month}
                    </button>
                  </td>
                  <td>{month.documents}</td>
                  <td>
                    <span className="badge complete">
                      <i className="dot complete" />
                      {month.complete}
                    </span>
                  </td>
                  <td>
                    <span className="badge needs_check">
                      <i className="dot needs_check" />
                      {month.needs_check}
                    </span>
                  </td>
                  <td>
                    <span className="badge manual_required">
                      <i className="dot manual_required" />
                      {month.manual_required}
                    </span>
                  </td>
                  <td>{money(month.net_income)}</td>
                  <td>{money(month.net_expenses)}</td>
                  <td>{money(month.net_result)}</td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="button small"
                      aria-label={`Open ${month.month}`}
                      onClick={() => {
                        navigate(`/months/${month.month}`);
                      }}
                    >
                      Open
                    </button>{" "}
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={`Export ${month.month}`}
                      title="Export Excel"
                      disabled={busy}
                      onClick={() => {
                        onExport(month.month);
                      }}
                    >
                      <Download size={15} />
                    </button>
                  </td>
                </tr>
              ))}
              {!months.length && (
                <tr>
                  <td colSpan={9}>No documents yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
function Month({
  month,
  busy,
  onExport,
  onRemove,
}: {
  month: FinanceMonth | undefined;
  busy: boolean;
  onExport: (month: string) => void;
  onRemove: (document: FinanceDocument) => void;
}) {
  if (!month)
    return (
      <section className="finance-card">
        <h2>Month not found</h2>
        <p>No documents are available for this month.</p>
      </section>
    );
  return (
    <>
      <header className="finance-heading">
        <div>
          <p className="eyebrow">Month detail</p>
          <h2>{month.month}</h2>
        </div>
        <button
          type="button"
          aria-label={`Export ${month.month}`}
          disabled={busy}
          onClick={() => {
            onExport(month.month);
          }}
        >
          Export Excel
        </button>
      </header>
      <Stats month={month} />
      <Documents
        title="Documents"
        documents={month.documents_list}
        busy={busy}
        onRemove={onRemove}
      />
    </>
  );
}
function Documents({
  title,
  documents,
  busy,
  onRemove,
  showMonth = false,
}: {
  title: string;
  documents: (FinanceDocument & { month?: string })[];
  busy: boolean;
  onRemove: (document: FinanceDocument) => void;
  showMonth?: boolean;
}) {
  return (
    <section className="panel">
      <div className="section-header">
        <h2>{title}</h2>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>File</th>
              {showMonth && <th>Month</th>}
              <th>Date</th>
              <th>Seller</th>
              <th>Buyer</th>
              <th>Direction</th>
              <th>Account</th>
              <th>Total</th>
              <th>Status</th>
              <th>Issues</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => {
              const issues = financeDocumentIssues(document, document.month);
              return (
                <tr
                  key={document.id}
                  className={issues.length ? "finance-suspicious" : undefined}
                >
                  <td className="left">
                    <span className="file-title">
                      {issues.length > 0 && (
                        <TriangleAlert
                          size={16}
                          className="warning-icon"
                          aria-label="Suspicious document"
                        />
                      )}
                      <strong>{document.filename}</strong>
                    </span>
                    <small>
                      {document.reference || document.processing_status}
                    </small>
                  </td>
                  {showMonth && <td>{document.month}</td>}
                  <td>{document.document_date || "-"}</td>
                  <td>{document.seller || "-"}</td>
                  <td>{document.buyer || "-"}</td>
                  <td>
                    <span
                      className={`direction-pill ${document.transaction_direction}`}
                    >
                      {document.transaction_direction}
                    </span>
                  </td>
                  <td className="left">
                    {document.account_number || "-"}
                    <small>{document.account_name}</small>
                  </td>
                  <td>
                    {document.currency} {document.gross_amount || "-"}
                  </td>
                  <td>
                    <span className={`badge ${document.review_status}`}>
                      <i className={`dot ${document.review_status}`} />
                      {status(document.review_status)}
                    </span>
                  </td>
                  <td>
                    {issues.length ? (
                      <div className="issue-list">
                        {issues.slice(0, 2).map((issue) => (
                          <span
                            key={issue.label}
                            className="issue-pill warning"
                            title={issue.detail}
                          >
                            <TriangleAlert size={13} />
                            {issue.label}
                          </span>
                        ))}
                        {issues.length > 2 && (
                          <span
                            title={issues
                              .slice(2)
                              .map((issue) => issue.detail)
                              .join("\n")}
                          >
                            +{issues.length - 2}
                          </span>
                        )}
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="button small"
                        aria-label={`Review ${document.filename}`}
                        onClick={() => {
                          navigate(`/documents/${document.id}`);
                        }}
                      >
                        Review
                      </button>
                      {document.delete_eligible === true && (
                        <button
                          type="button"
                          className="icon-button danger"
                          aria-label={`Delete ${document.filename}`}
                          disabled={busy}
                          onClick={() => {
                            onRemove(document);
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!documents.length && (
              <tr>
                <td colSpan={showMonth ? 11 : 10} className="empty-cell">
                  No documents need review.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function PreviewPane({
  detail,
  clientId,
}: {
  detail: FinanceDocumentDetail;
  clientId: string;
}) {
  const canvas = useRef<HTMLDivElement>(null);
  const drag = useRef({
    active: false,
    x: 0,
    y: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fitWidth, setFitWidth] = useState(true);
  const [checkingPage, setCheckingPage] = useState(false);
  const [knownPageCount, setKnownPageCount] = useState<number | null>(null);
  const [hasNextPage, setHasNextPage] = useState<boolean | null>(null);
  const [dragging, setDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(900);
  const [previewMessage, setPreviewMessage] = useState<string>();
  const preview = (nextPage: number) =>
    api.financeDocumentPreviewUrl(detail.id, clientId, nextPage);
  const original = api.financeDocumentOriginalUrl(detail.id, clientId);

  useEffect(() => {
    setPage(1);
    setZoom(1);
    setRotation(0);
    setFitWidth(true);
    setPreviewMessage(undefined);
    setKnownPageCount(detail.is_pdf ? null : 1);
    setHasNextPage(detail.is_pdf ? null : false);
  }, [detail.id, detail.is_pdf]);

  useEffect(() => {
    if (!detail.is_pdf || knownPageCount !== null || hasNextPage !== null)
      return;
    let active = true;
    setCheckingPage(true);
    void fetch(preview(page + 1), {
      cache: "no-store",
      credentials: "same-origin",
    })
      .then((response) => {
        if (!active) return;
        if (response.ok) setHasNextPage(true);
        else if (response.status === 404) setKnownPageCount(page);
        else setPreviewMessage("The next preview page could not be checked.");
      })
      .catch(() => {
        if (active)
          setPreviewMessage("The next preview page could not be checked.");
      })
      .finally(() => {
        if (active) setCheckingPage(false);
      });
    return () => {
      active = false;
    };
  }, [clientId, detail.id, detail.is_pdf, hasNextPage, knownPageCount, page]);

  useEffect(() => {
    if (!canvas.current) return;
    const element = canvas.current;
    const update = () => {
      setContainerWidth(Math.max(320, element.clientWidth - 40));
    };
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  const nextPage = () => {
    if (knownPageCount !== null ? page >= knownPageCount : hasNextPage !== true)
      return;
    setPreviewMessage(undefined);
    setHasNextPage(null);
    setPage((current) => current + 1);
  };
  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !canvas.current) return;
    drag.current = {
      active: true,
      x: event.clientX,
      y: event.clientY,
      scrollLeft: canvas.current.scrollLeft,
      scrollTop: canvas.current.scrollTop,
    };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current.active || !canvas.current) return;
    event.preventDefault();
    canvas.current.scrollLeft =
      drag.current.scrollLeft - (event.clientX - drag.current.x);
    canvas.current.scrollTop =
      drag.current.scrollTop - (event.clientY - drag.current.y);
  };
  const stopDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    drag.current.active = false;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const imageWidth = fitWidth
    ? Math.round(containerWidth * zoom)
    : Math.round(900 * zoom);
  const canAdvance =
    knownPageCount !== null ? page < knownPageCount : hasNextPage === true;
  const pageLabel =
    knownPageCount !== null
      ? `Page ${page} of ${knownPageCount}`
      : hasNextPage
        ? `Page ${page} of at least ${page + 1}`
        : `Page ${page} of unknown total`;

  return (
    <section className="panel preview-panel">
      <div className="preview-toolbar">
        <div className="preview-title">
          <FileText aria-hidden="true" size={16} /> Original document
        </div>
        <div className="preview-tools">
          <button
            type="button"
            className="icon-button"
            aria-label="Zoom out"
            onClick={() => {
              setZoom((value) => Math.max(0.5, value - 0.1));
            }}
          >
            <ZoomOut aria-hidden="true" size={16} />
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label="Zoom in"
            onClick={() => {
              setZoom((value) => Math.min(2.5, value + 0.1));
            }}
          >
            <ZoomIn aria-hidden="true" size={16} />
          </button>
          <button
            type="button"
            className={`icon-button${fitWidth ? " active" : ""}`}
            aria-label="Fit width"
            aria-pressed={fitWidth}
            onClick={() => {
              setFitWidth((value) => !value);
            }}
          >
            <Maximize2 aria-hidden="true" size={16} />
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label="Rotate"
            onClick={() => {
              setRotation((value) => (value + 90) % 360);
            }}
          >
            <RotateCw aria-hidden="true" size={16} />
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => {
              setPage((value) => Math.max(1, value - 1));
              setPreviewMessage(undefined);
            }}
          >
            <ChevronLeft aria-hidden="true" size={16} />
          </button>
          <span
            className="page-count"
            aria-live="polite"
            aria-label={pageLabel}
          >
            {pageLabel}
          </span>
          <button
            type="button"
            className="icon-button"
            aria-label="Next page"
            disabled={checkingPage || !canAdvance}
            onClick={() => {
              nextPage();
            }}
          >
            <ChevronRight aria-hidden="true" size={16} />
          </button>
          <a
            className="icon-button"
            href={original}
            target="_blank"
            rel="noreferrer"
            aria-label="Download original"
          >
            <Download aria-hidden="true" size={16} />
          </a>
        </div>
      </div>
      {previewMessage && (
        <p className="preview-message" role="status">
          {previewMessage}
        </p>
      )}
      <div
        ref={canvas}
        className={`document-preview${fitWidth ? " fit-width" : ""}${dragging ? " dragging" : ""}`}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
      >
        <div className="preview-stage">
          <img
            src={preview(page)}
            alt={detail.filename}
            draggable={false}
            onError={() => {
              setPreviewMessage("Preview unavailable.");
            }}
            style={{
              width: `${imageWidth}px`,
              transform: `rotate(${rotation}deg)`,
            }}
          />
        </div>
      </div>
    </section>
  );
}

function DocumentReview({
  detail,
  clientId,
  busy,
  onSave,
  onStatus,
}: {
  detail: FinanceDocumentDetail | undefined;
  clientId: string;
  busy: boolean;
  onSave: (payload: Record<string, unknown>) => void;
  onStatus: (
    payload: Record<string, unknown>,
    status: FinanceDocument["review_status"],
  ) => void;
}) {
  const [draft, setDraft] = useState<ExtractionPayload>(emptyPayload);
  useEffect(() => {
    setDraft(normalizePayload(detail?.payload, detail?.filename || ""));
  }, [detail]);
  if (!detail)
    return (
      <section className="finance-card">
        <h2>Loading document</h2>
        <p>Loading extracted values…</p>
      </section>
    );
  const update = (path: string, value: string) => {
    setDraft(setDeep(draft, path, value || null));
  };
  const updateLine = (index: number, key: keyof LineItem, value: string) => {
    const next = structuredClone(draft);
    const lines = next.line_items.length
      ? [...next.line_items]
      : [emptyLineItem()];
    lines[index] = {
      ...(lines[index] || emptyLineItem()),
      [key]: value || null,
    };
    next.line_items = lines;
    setDraft(next);
  };
  const updateAccount = (accountNumber: string) => {
    const account = detail.accounts.find(
      (item) => item.account_number === accountNumber,
    );
    setDraft({
      ...draft,
      account_number: account?.account_number || null,
      account_name: account?.account_name || null,
      classification_status: account ? "manual" : "needs_review",
      account_reasoning: account ? "Manually selected by accountant." : null,
    });
  };
  return (
    <div className="document-page">
      <header className="finance-document-heading">
        <div>
          <p className="eyebrow">{detail.document_month || "Unassigned"}</p>
          <h2>{detail.filename}</h2>
        </div>
        <span className={`badge ${detail.review_status}`}>
          <i className={`dot ${detail.review_status}`} />
          {status(detail.review_status)}
        </span>
      </header>
      {detail.error_message && <Notice error>{detail.error_message}</Notice>}
      <section className="review-layout">
        <PreviewPane detail={detail} clientId={clientId} />
        <form
          className="panel editor-panel"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(draft);
          }}
        >
          <div className="section-header">
            <h2>Extracted values</h2>
          </div>
          <div className="accounting-strip">
            <label className="plain-field">
              <span>Direction</span>
              <select
                value={draft.transaction_direction}
                onChange={(event) => {
                  update("transaction_direction", event.target.value);
                }}
              >
                <option value="unknown">Unknown</option>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </label>
            <label
              className={`plain-field account-select ${draft.classification_status === "needs_review" ? "needs_check" : "complete"}`}
            >
              <span>Chart account</span>
              <select
                value={draft.account_number || ""}
                onChange={(event) => {
                  updateAccount(event.target.value);
                }}
              >
                <option value="">Needs review</option>
                {detail.accounts.map((account) => (
                  <option
                    key={account.account_number}
                    value={account.account_number}
                  >
                    {account.account_number} - {account.account_name}
                  </option>
                ))}
              </select>
            </label>
            <div className="account-hint">
              <strong>{draft.account_number || "No account"}</strong>
              <span>{draft.account_name || "Classification needs review"}</span>
              <small>
                {confidenceLabel(draft.account_confidence)}{" "}
                {draft.account_reasoning || ""}
              </small>
            </div>
          </div>
          <div className="field-grid three">
            <Field
              label="Document date"
              value={draft.document_date}
              onChange={(value) => {
                update("document_date", value);
              }}
              critical
            />
            <Field
              label="Document type"
              value={draft.document_type}
              onChange={(value) => {
                update("document_type", value);
              }}
            />
            <Field
              label="Reference / invoice no."
              value={draft.invoice_or_reference_number}
              onChange={(value) => {
                update("invoice_or_reference_number", value);
              }}
              critical
            />
            <Field
              label="Seller name"
              value={draft.seller.name}
              onChange={(value) => {
                update("seller.name", value);
              }}
              critical
            />
            <Field
              label="Seller VAT ID"
              value={draft.seller.vat_id}
              onChange={(value) => {
                update("seller.vat_id", value);
              }}
            />
            <Field
              label="Seller tax ID"
              value={draft.seller.tax_id}
              onChange={(value) => {
                update("seller.tax_id", value);
              }}
            />
            <Field
              label="Buyer name"
              value={draft.buyer.name}
              onChange={(value) => {
                update("buyer.name", value);
              }}
              critical
            />
            <Field
              label="Buyer VAT ID"
              value={draft.buyer.vat_id}
              onChange={(value) => {
                update("buyer.vat_id", value);
              }}
            />
            <Field
              label="Buyer tax ID"
              value={draft.buyer.tax_id}
              onChange={(value) => {
                update("buyer.tax_id", value);
              }}
            />
            <Field
              label="Net amount"
              value={draft.net_amount}
              onChange={(value) => {
                update("net_amount", value);
              }}
            />
            <Field
              label="VAT rate"
              value={draft.vat_rate}
              onChange={(value) => {
                update("vat_rate", value);
              }}
            />
            <Field
              label="VAT amount"
              value={draft.vat_amount}
              onChange={(value) => {
                update("vat_amount", value);
              }}
            />
            <Field
              label="Gross amount"
              value={draft.gross_amount}
              onChange={(value) => {
                update("gross_amount", value);
              }}
              critical
            />
            <Field
              label="Currency"
              value={draft.currency}
              onChange={(value) => {
                update("currency", value);
              }}
              critical
            />
          </div>
          <h3>Organization details</h3>
          <div className="field-grid two">
            <Field
              label="Seller address"
              value={draft.seller.address}
              onChange={(value) => {
                update("seller.address", value);
              }}
            />
            <Field
              label="Buyer address"
              value={draft.buyer.address}
              onChange={(value) => {
                update("buyer.address", value);
              }}
            />
            <Field
              label="Seller company ID"
              value={draft.seller.company_id}
              onChange={(value) => {
                update("seller.company_id", value);
              }}
            />
            <Field
              label="Buyer company ID"
              value={draft.buyer.company_id}
              onChange={(value) => {
                update("buyer.company_id", value);
              }}
            />
            <Field
              label="Seller IBAN"
              value={draft.seller.bank.iban}
              onChange={(value) => {
                update("seller.bank.iban", value);
              }}
            />
            <Field
              label="Buyer IBAN"
              value={draft.buyer.bank.iban}
              onChange={(value) => {
                update("buyer.bank.iban", value);
              }}
            />
            <Field
              label="Seller BIC/SWIFT"
              value={draft.seller.bank.bic_swift}
              onChange={(value) => {
                update("seller.bank.bic_swift", value);
              }}
            />
            <Field
              label="Buyer BIC/SWIFT"
              value={draft.buyer.bank.bic_swift}
              onChange={(value) => {
                update("buyer.bank.bic_swift", value);
              }}
            />
          </div>
          <div className="line-header">
            <h3>Line items</h3>
            <button
              type="button"
              className="button small"
              onClick={() => {
                setDraft({
                  ...draft,
                  line_items: [...draft.line_items, emptyLineItem()],
                });
              }}
            >
              Add item
            </button>
          </div>
          <div className="table-scroll">
            <table className="line-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Account</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Unit price</th>
                  <th>Net</th>
                  <th>VAT %</th>
                  <th>VAT</th>
                  <th>Gross</th>
                </tr>
              </thead>
              <tbody>
                {(draft.line_items.length
                  ? draft.line_items
                  : [emptyLineItem()]
                ).map((item, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        value={item.description || ""}
                        onChange={(event) => {
                          updateLine(index, "description", event.target.value);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        value={
                          item.account_number || draft.account_number || ""
                        }
                        onChange={(event) => {
                          updateLine(
                            index,
                            "account_number",
                            event.target.value,
                          );
                        }}
                      />
                    </td>
                    <td>
                      <input
                        value={item.quantity || ""}
                        onChange={(event) => {
                          updateLine(index, "quantity", event.target.value);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        value={item.unit || ""}
                        onChange={(event) => {
                          updateLine(index, "unit", event.target.value);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        value={item.unit_price || ""}
                        onChange={(event) => {
                          updateLine(index, "unit_price", event.target.value);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        value={item.net_amount || ""}
                        onChange={(event) => {
                          updateLine(index, "net_amount", event.target.value);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        value={item.vat_rate || ""}
                        onChange={(event) => {
                          updateLine(index, "vat_rate", event.target.value);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        value={item.vat_amount || ""}
                        onChange={(event) => {
                          updateLine(index, "vat_amount", event.target.value);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        value={item.gross_amount || ""}
                        onChange={(event) => {
                          updateLine(index, "gross_amount", event.target.value);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="editor-actions">
            <button type="submit" className="button primary" disabled={busy}>
              <Save size={16} /> Save
            </button>
            <button
              type="button"
              className="button success"
              onClick={() => {
                onStatus(draft, "complete");
              }}
              disabled={busy}
            >
              <Check size={16} /> Mark complete
            </button>
            <button
              type="button"
              className="button warning"
              onClick={() => {
                onStatus(draft, "needs_check");
              }}
              disabled={busy}
            >
              <CircleAlert size={16} /> Mark needs check
            </button>
            <button
              type="button"
              className="button danger"
              onClick={() => {
                onStatus(draft, "manual_required");
              }}
              disabled={busy}
            >
              <CircleAlert size={16} /> Mark manual
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  critical = false,
}: {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  critical?: boolean;
}) {
  const state = fieldStatus(value, critical);
  return (
    <label className={`field ${state}`}>
      <span>
        <i className={`dot ${state}`} />
        {label}
      </span>
      <input
        value={value || ""}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </label>
  );
}
